import type { ItemFieldEdit } from './itemEdits';

/**
 * Journal record types for routine editor undo/redo.
 *
 * An entry is a field-scoped record of what a single user action changed, not an
 * inverse command. Undo builds a patch from every `before`, redo from every
 * `after` — which is the write model the routines service already speaks, so
 * there is no per-operation inverse to hand-write and none to rot as new step
 * kinds land.
 *
 * The same record shape is what durable version history persists: label, author,
 * timestamp, and field-scoped before/after are exactly a commit.
 */

/**
 * Marks a field as not present in the document, as distinct from present and
 * holding `undefined`. Maps to `$unset` when building a patch.
 */
export const ABSENT = Symbol('routine-edit/absent');

export type Absent = typeof ABSENT;

export interface FieldChange {
  /** Dotted path into the routine document, e.g. `stepConfigurations.<stepID>`. */
  path: string;
  before: unknown | Absent;
  after: unknown | Absent;
}

/** Which editor surface an entry belongs to. */
export type EditPanel = 'steps' | 'config' | 'space' | 'variables';

/**
 * Where the editor should take the user when an entry is undone or redone. Held
 * on the entry so components never have to sniff the diff to work it out.
 */
export interface EditFocus {
  panel: EditPanel;
  /** Every affected step — all of them flash, not just the first. */
  stepIDs?: string[];
  /** Space item or variable ID. */
  itemID?: string;
  /** The setting to flash in the right panel. */
  fieldPath?: string;
}

/**
 * A toast description stored structured rather than pre-rendered, so it can be
 * read out with the direction applied. A pre-rendered string would say
 * "On → Off" when *redoing* the change that turned it off, which is the exact
 * ambiguity the toast exists to remove.
 *
 * `before`/`after` always describe the *original* edit; the renderer swaps them
 * for undo. Same for the verb pair.
 */
export interface EditDescription {
  /** The thing that changed, e.g. "3. Move arm". */
  subject: string;
  /** Verb when the edit is applied forward (redo), e.g. "deleted". */
  verbForward: string;
  /** Verb when the edit is reversed (undo), e.g. "restored". */
  verbReverse: string;
  /** The setting that changed, e.g. "Stop here". */
  field?: string;
  /** Value before the original edit, e.g. "On". */
  before?: string;
  /** Value after the original edit, e.g. "Off". */
  after?: string;
  /**
   * Free-form trailing clause used where a before/after pair reads poorly, e.g.
   * "With the 3 steps inside it".
   */
  detail?: string;
}

/**
 * `system` entries are never recorded. The routine document is remote and
 * reactive — a write echoes back through the subscription and re-renders
 * components, some of which write again (default step config on first mount,
 * stop-early reference invalidation). Recording those would let undo walk
 * backwards into machine-generated writes the user never made.
 */
export type EditOrigin = 'user' | 'agent' | 'system';

export interface RoutineEditEntry {
  id: string;
  /** Menu/history label, e.g. "Deleted Loop", "Pasted 3 steps". */
  label: string;
  timestamp: Date;
  changes: FieldChange[];
  focus: EditFocus;
  describe: EditDescription;
  /**
   * Item- and field-scoped intent for the collections that support it, replayed
   * against the live document instead of restoring `changes` wholesale.
   *
   * Present only for entries that do not write `steps`. When absent the entry
   * applies through `changes` exactly as before, which is what a structural
   * edit still does.
   */
  itemEdits?: ItemFieldEdit[];
  /**
   * Consecutive entries sharing a key merge into one, so a slider drag or
   * typing "30" over "2" is a single undo. Absent means never merge.
   */
  coalesceKey?: string;
  origin: EditOrigin;
  /**
   * The `routineEdits` journal rows this entry was built from. Undoing the
   * entry marks them undone on the server, so a later open of the routine does
   * not offer the same undo again; redo clears the mark.
   */
  journalIDs?: string[];
  /**
   * Refuse to apply unless the live document still holds what the entry
   * expects: the step tree's shape for a tree restore, the current value for
   * any whole field it writes. Set on entries that may predate everything this
   * editor has seen of the document — applying one over work done since would
   * silently discard that work.
   */
  requiresStepTreeMatch?: boolean;
}

export type EditDirection = 'undo' | 'redo';
